import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';

export const useShares = (options = {}) => {
  const queryClient = useQueryClient();
  const { search, status, page = 1, per_page = 10 } = options;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['shares', { search, status, page, per_page }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (status && status !== 'all') params.append('status', status);
      if (page) params.append('page', page.toString());
      if (per_page) params.append('per_page', per_page.toString());
      
      const response = await api.get(`/shares?${params.toString()}`);
      return response.data;
    },
  });

  const addShareMutation = useMutation({
    mutationFn: async (shareData) => {
      const response = await api.post('/shares', shareData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const updateShareMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/shares/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const updateShareStatusMutation = useMutation({
    mutationFn: async ({ share_id, status }) => {
      const response = await api.put(`/shares/${share_id}/status`, { status });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const deleteShareMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/shares/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const getMemberShares = useQuery({
    queryKey: ['memberShares', options.member_id],
    queryFn: async () => {
      if (!options.member_id) throw new Error('Member ID is required');
      const response = await api.get(`/shares/member/${options.member_id}`);
      return response.data;
    },
    enabled: !!options.member_id,
  });

  const getSharesStatistics = useQuery({
    queryKey: ['sharesStatistics', options],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      // Add filters to query params for statistics
      if (options.member_id) params.append('member_id', options.member_id.toString());
      if (options.status && options.status !== 'all') params.append('status', options.status);
      if (options.start_date_from) params.append('start_date_from', options.start_date_from);
      if (options.start_date_to) params.append('start_date_to', options.start_date_to);
      
      const response = await api.get(`/shares/statistics?${params.toString()}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    data,
    shares: data?.data || [],
    meta: data?.meta,
    isLoading,
    error,
    refetch,
    addShare: addShareMutation.mutateAsync,
    updateShare: updateShareMutation.mutateAsync,
    updateShareStatus: updateShareStatusMutation,
    deleteShare: deleteShareMutation.mutateAsync,
    memberShares: getMemberShares.data?.data,
    isLoadingMemberShares: getMemberShares.isLoading,
    statistics: getSharesStatistics.data?.data,
    isLoadingStatistics: getSharesStatistics.isLoading,
  };
};

// Hook to export shares
export const useExportShares = () => {
  return useMutation({
    mutationFn: async (filters = {}) => {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      
      const response = await api.get(`/shares/export?${params.toString()}`, {
        responseType: 'blob',
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Use the filename from the backend response headers if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'shares';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // If no filename from headers, determine from type parameter
      if (filename === 'shares') {
        const type = filters.type || 'excel';
        const extensions = {
          'excel': 'xlsx',
          'csv': 'csv', 
          'pdf': 'pdf'
        };
        filename = `shares.${extensions[type] || 'xlsx'}`;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error) || 'Failed to export shares');
    },
  });
}; 